import axios from 'axios'
import { env } from '../config/env'
import i18n from '../i18n'
import { useToastStore } from '../stores/toast'
import { useAuthStore } from '../stores/auth'

const api = axios.create({
  baseURL: env.api.url,
  timeout: env.api.timeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: false
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const locale = (i18n.global.locale as any).value || i18n.global.locale
    if (locale && config.headers) {
      config.headers['Accept-Language'] = locale
    }

    if (config.headers) {
      config.headers['ngrok-skip-browser-warning'] = 'true'
    }

    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => Promise.reject(error)
)

let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

const clearAuthAndRedirect = () => {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('user')
  localStorage.removeItem('user_init_data')
  localStorage.removeItem('impersonation')
  // /admin-login shows its own error instead of jumping to the login page
  if (window.location.pathname !== '/login' && window.location.pathname !== '/admin-login') {
    window.location.href = '/login'
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      const token = localStorage.getItem('auth_token')

      // No token at all — go to login immediately
      if (!token) {
        clearAuthAndRedirect()
        return Promise.reject(error)
      }

      // This was the refresh endpoint itself — token fully expired, logout
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearAuthAndRedirect()
        return Promise.reject(error)
      }

      originalRequest._retry = true

      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve) => {
          refreshQueue.push((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            resolve(api(originalRequest))
          })
        })
      }

      isRefreshing = true

      try {
        const response = await api.post('v1/auth/refresh')
        const newToken = response.data.access_token
        localStorage.setItem('auth_token', newToken)

        // Replay queued requests with new token
        refreshQueue.forEach((cb) => cb(newToken))
        refreshQueue = []

        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch {
        refreshQueue = []
        clearAuthAndRedirect()
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    if (error.response?.status === 403) {
      if (error.response.data?.error_code === 'DEMO_ACCOUNT_RESTRICTED') {
        const toastStore = useToastStore()
        toastStore.error((i18n.global.t as (key: string) => string)('demo.restrictions.restricted'))
        return Promise.reject(error)
      }

      if (error.response.data?.error === 'club_access_expired') {
        const exemptPaths = ['/payment-access', '/no-access', '/', '/home']
        if (!exemptPaths.includes(window.location.pathname)) {
          const authStore = useAuthStore()
          const role = authStore.initData?.persons?.current?.role
          if (role === 'OWNER' || role === 'SUPERVISOR') {
            window.location.href = '/payment-access'
          } else {
            window.location.href = '/no-access'
          }
        }
        return Promise.reject(error)
      }

      // Public club signup page handles "signups disabled" itself
      if (error.response.data?.error === 'signup_disabled') {
        return Promise.reject(error)
      }

      console.error('Access denied:', error.response.data)
      if (window.location.pathname !== '/' && window.location.pathname !== '/home') {
        window.location.href = '/'
      }
    }

    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  }
)

export default api
